/**
 * Header Component
 * Top header med navigation och actions
 * Anpassad från deque-web-main
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ROUTES } from '@/constants/routes';
import ThemeToggle from './ThemeToggle';
import LanguageSwitcher from '../common/LanguageSwitcher';
import VersionDropdown from '../common/VersionDropdown';
import ImpersonationDropdown from './ImpersonationDropdown';
import { useTranslation } from '@/store/languageStore';
import { MagnifyingGlassIcon, BookOpenIcon, BeakerIcon } from '@heroicons/react/24/outline';
import PersonalTrackerAlarmBell from './PersonalTrackerAlarmBell';
import { PageBackButton } from '@/components/common/PageBackButton';
import { getConfig } from '@/services/api/configService';
import { resolveViewTitle } from '@/utils/documentTitle';
import { useTheme } from 'next-themes';

const Header = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showTestRunner, setShowTestRunner] = useState(false);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  
  // Färger för header-ikoner - grå i ljust läge, färgade i mörkt läge
  const iconColors = {
    search: isDark ? '#60a5fa' : '#6b7280',      // blue / gray
    notification: isDark ? '#fbbf24' : '#6b7280', // amber / gray
    manual: isDark ? '#34d399' : '#6b7280',      // emerald / gray
    test: isDark ? '#a78bfa' : '#6b7280',        // purple / gray
  };

  // Hämta konfiguration vid mount och när location ändras (för att uppdatera om användaren loggar in/ut)
  useEffect(() => {
    getConfig()
      .then(config => {
        setShowTestRunner(config.showTestRunner);
      })
      .catch(error => {
        console.error('Error loading config:', error);
        // Dölj test runner vid fel (säkrast)
        setShowTestRunner(false);
      });
  }, [location.pathname]); // Uppdatera när location ändras

  const pageTitle = resolveViewTitle(pathname, t) ?? 'DQFleet';

  return (
    <div className="flex flex-col relative bg-bgContainer justify-between border-border border-b-[1px] w-full transition-all duration-400 ease-in-out">
      <div className="text-text-default py-2 px-4 text-2xl flex justify-between items-center font-semibold">
        <div className="flex items-center gap-2 min-w-0">
          {pathname !== ROUTES.HOME && pathname !== ROUTES.DASHBOARD && (
            <PageBackButton className="p-2 hover:bg-bgLight rounded-lg transition-colors shrink-0" />
          )}
          <div className="truncate">{pageTitle}</div>
        </div>
        <div className="flex items-center gap-2 relative">
          <button
            type="button"
            className="h-8 w-8 text-[16px] cursor-pointer rounded-full hover:bg-bgLight transition-colors flex items-center justify-center"
            aria-label="Search"
          >
            <span style={{ color: iconColors.search, display: 'inline-flex' }}>
              <MagnifyingGlassIcon className="w-5 h-5" />
            </span>
          </button>
          <PersonalTrackerAlarmBell iconColor={iconColors.notification} />
          <button
            type="button"
            onClick={() => navigate(ROUTES.USER_MANUAL)}
            className="h-8 w-8 text-[16px] cursor-pointer rounded-full hover:bg-bgLight transition-colors flex items-center justify-center"
            aria-label={t('manual.title')}
            title={t('manual.title')}
          >
            <span style={{ color: iconColors.manual, display: 'inline-flex' }}>
              <BookOpenIcon className="w-5 h-5" />
            </span>
          </button>
          {showTestRunner && (
            <button
              type="button"
              onClick={() => navigate(ROUTES.TEST_RUNNER)}
              className="h-8 w-8 text-[16px] cursor-pointer rounded-full hover:bg-bgLight transition-colors flex items-center justify-center"
              aria-label="Test Runner"
              title="Test Runner"
            >
              <span style={{ color: iconColors.test, display: 'inline-flex' }}>
                <BeakerIcon className="w-5 h-5" />
              </span>
            </button>
          )}
          <ImpersonationDropdown />
          <span data-guide="header-version"><VersionDropdown /></span>
          <span data-guide="header-language"><LanguageSwitcher /></span>
          <span data-guide="header-theme"><ThemeToggle /></span>
        </div>
      </div>
    </div>
  );
};

export default Header;
